import { Box } from "@mui/material";
import { useState } from "react";
import { FlexCenterWrapper } from "../../../../../components/DefaultNoDataWrapper";
import Flex from "../../../../../components/Flex";
import OutlineCard from "../../../../../components/OutlineCard";
import { ScrollArea } from "../../../../../components/ScrollBar";
import Text from "../../../../../components/Text";
import ConfirmationModal from "../../../../../components/app/ConfirmationModal";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import NewPatientIntakeForm from "../../../../../pages/app/provider/scheduling/intake-form/NewPatientIntakeForm";
import { useValidateDob } from "../dob-validation/useValidateDob";
import PublicAssessmentsSideTab from "./PublicAssessmentsSideTab";
import { ScreeningFullViewForm } from "./form/ScreeningFullViewForm";
import { ValidateDob } from "../validateDob";

/**
 * @author [Rajkumar Rathod]
 */
const PublicPatientAssessment = () => {
  const showSnackbar = useSnackbar();
  const patientId = useQueryParams("patientId");
  const assessmentTabs = useQueryParams("assessments")?.split(",");
  const intakeForms = useQueryParams("intakeform")?.split(",");
  const isIntakeForms = !intakeForms?.includes("null") && !!intakeForms?.length;
  const [activeTab, setActiveTab] = useState<{ ind: number; val: string }>({
    ind: 0,
    val: isIntakeForms ? "intakeForm" : "screening",
  });

  const [activeChildTab, setActiveChildTab] = useState<{
    ind: number;
    val: any;
  }>({ ind: 0, val: assessmentTabs?.[0] });
  const { birthDate, setBirthDate, isAuthenticate, onVerify } = useValidateDob(
    patientId,
    showSnackbar
  );
  const confirmationDisc = useDisclosure();

  const mainTab = isIntakeForms ? ["intakeForm", "screening"] : ["screening"];

  const handleTabChange = ({
    ind,
    childInd,
  }: {
    ind: number;
    childInd: number;
  }) => {
    setActiveChildTab({
      ind: childInd ?? 0,
      val: assessmentTabs?.[childInd ?? 0] ?? "",
    });
    setActiveTab({ ind, val: mainTab?.[ind] ?? "" });
  };

  const CurrentActiveScreen: Record<string, React.ReactNode> = {
    intakeForm: <NewPatientIntakeForm isPublic setActiveTab={setActiveTab} />,
    screening: (
      <ScreeningFullViewForm
        onSkip={(ind, prev) => {
          setActiveChildTab({
            ind: prev ? ind - 1 : ind + 1,
            val: assessmentTabs?.[prev ? ind - 1 : ind + 1] ?? "",
          });
        }}
        isPublicView
        activeTab={activeChildTab}
        onSave={(ind) => {
          if (ind !== (assessmentTabs && assessmentTabs.length - 1)) {
            setActiveChildTab({
              ind: ind + 1,
              val: assessmentTabs?.[ind + 1] ?? "",
            });
          } else setActiveChildTab({ ind: 0, val: assessmentTabs?.[0] ?? "" });
        }}
      />
    ),
  };

  return (
    <Flex
      height={"100%"}
      width={"100%"}
      flexDirection={{ xs: "column", sm: "row" }}
    >
      <ConfirmationModal
        maxWidth={"sm"}
        onConfirm={() => {
          handleTabChange({
            ind: confirmationDisc.data?.ind ?? 0,
            childInd: confirmationDisc.data?.childInd ?? 0,
          });

          confirmationDisc.close();
        }}
        disclosureHost={confirmationDisc}
        confirmationText="Yes"
        cancelText="No"
        title={
          <Text fontWeight={"medium"} fontSize={"24px"}>
            Are you sure You want to leave? Changes won't be saved.
          </Text>
        }
      />

      {/* patient side bar */}
      {isAuthenticate && (
        <Box p={{ xs: 1, sm: 0.8 }} height={{ xs: "auto", sm: "100%" }}>
          <OutlineCard
            sx={{
              width: { xs: "100%", sm: "286px" },
              padding: { xs: "2px", sm: 2 },
              height: { xs: "auto", sm: "100%" },
            }}
            elevation={5}
          >
            <PublicAssessmentsSideTab
              activeTab={activeTab}
              activeChildTab={activeChildTab}
              handleTabChange={({ ind, childInd }) => {
                // check for screening tab
                if (ind === 1) {
                  confirmationDisc.open({ ind, childInd });
                } else {
                  handleTabChange({ ind, childInd: childInd ?? 0 });
                }
              }}
            />
          </OutlineCard>
        </Box>
      )}

      {isAuthenticate ? (
        <Box p={0.8} width={"100%"} minWidth={0}>
          <OutlineCard style={{ height: "100%" }}>
            <ScrollArea height={"100%"}>
              {CurrentActiveScreen[activeTab.val]}
            </ScrollArea>
          </OutlineCard>
        </Box>
      ) : (
        <FlexCenterWrapper>
          <ValidateDob
            birthDate={birthDate}
            setBirthDate={setBirthDate}
            onVerify={() => onVerify()}
          />
        </FlexCenterWrapper>
      )}
    </Flex>
  );
};

export default PublicPatientAssessment;
